<script setup>
import { useI18n } from 'vue-i18n'
import config from '@/config'

const { t } = useI18n()
const VERSION = config.VITE_VERSION
</script>
<template>
  <div class="login-page">
    <div class="login-page__card">
      <div class="login-page__logo">
        <img
          src="@/assets/logo_black.svg"
          :alt="t('common.logoAlt')"
          class="login-page__logo-img"
        />
      </div>

      <slot />

      <div class="login-page__footer">
        <p class="login-page__footer-text">
          SOYKA:LTMS {{ VERSION }}<br />
          {{ t('common.developedBy') }}
          <a
            href="//atd.systems"
            target="_blank"
            rel="noopener noreferrer"
            class="login-page__footer-link"
            >ATD.Systems</a
          >
        </p>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
/*
  scrollbar-gutter: stable на html оставляет полоску справа вне fixed-оверлея —
  заливка html/body тем же тоном, что у фона login.
*/
html:has(.login-page),
body:has(.login-page) {
  background-color: #071b3a;
}

.login-page {
  /*
    При CSS zoom на <html> 100vh ≠ экран (белая полоса снизу).
    Как blur/LP-модалка: только inset:0 + width/height:auto.
  */
  position: fixed;
  inset: 0;
  width: auto;
  height: auto;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  overflow: auto;
  box-sizing: border-box;
  background-color: #071b3a;
  background-image: url(@/assets/back3.jpg);
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
}

.login-page__card {
  width: 100%;
  max-width: 420px;
  padding: 2.5rem;
  background: #fff;
  border: 1.5px solid rgba(26, 57, 118, 0.12);
  border-radius: var(--radius-lg, 20px);
  box-shadow: 0 8px 32px rgba(26, 57, 118, 0.08);

  form {
    .btn-primary {
      width: 100%;
    }

    .logout-button {
      float: none;
      width: 100%;
      margin-top: 0.5rem;
    }
  }

  h1 {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--text-primary, #1d1d1f);
    margin-bottom: 1.25rem;
  }
}

.login-page__logo {
  text-align: center;
  margin-bottom: 2rem;
}

.login-page__logo-img {
  height: 52px;
  width: auto;
}

.login-page__footer {
  margin-top: 2rem;
  padding-top: 1.25rem;
  border-top: 0.5px solid var(--border-subtle, rgba(0, 0, 0, 0.06));
  text-align: center;
}

.login-page__footer-text {
  margin: 0;
  font-size: 0.72rem;
  color: var(--text-tertiary, #aeaeb2);
  line-height: 1.7;
}

.login-page__footer-link {
  color: var(--berry, #1a3976);
  font-weight: 600;
  text-decoration: none;

  &:hover {
    color: var(--accent-cyan, #68d2df);
  }
}

/* Опции выбора способа входа (макет login.html) */
.login-page__status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  margin-bottom: 1.5rem;
}

.login-page__status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--success, #30d158);
  animation: login-page-pulse 2.5s ease-in-out infinite;
}

@keyframes login-page-pulse {
  0%,
  100% {
    opacity: 1;
    box-shadow: 0 0 0 0 rgba(48, 209, 88, 0.5);
  }
  50% {
    opacity: 0.8;
    box-shadow: 0 0 0 5px rgba(48, 209, 88, 0);
  }
}

.login-page__status-text {
  font-size: 0.7rem;
  color: var(--text-tertiary, #aeaeb2);
  font-weight: 500;
}

.login-page__title {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-secondary, #86868b);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 1rem;
}

.login-page__option {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.9rem 1.2rem;
  border-radius: 12px;
  border: 0.5px solid var(--border-subtle, rgba(0, 0, 0, 0.06));
  background: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  margin-bottom: 0.75rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  text-decoration: none;
  color: inherit;

  &:last-of-type {
    margin-bottom: 0;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(26, 57, 118, 0.1);
    color: inherit;
  }
}

.login-page__option-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  margin-right: 1rem;
  color: #fff;
  flex-shrink: 0;

  &--microsoft {
    background: linear-gradient(135deg, #0078d4, #00bcf2);
  }

  &--external {
    background: linear-gradient(135deg, var(--berry, #1a3976), var(--berry-light, #2d5a9e));
  }
}

.login-page__option-body {
  text-align: left;
  min-width: 0;
}

.login-page__option-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-primary, #1d1d1f);
}

.login-page__option-hint {
  font-size: 0.72rem;
  color: var(--text-tertiary, #aeaeb2);
}

.login-page__back {
  display: flex;
  justify-content: center;
  margin-top: 0.75rem;
  margin-bottom: 0;

  .btn-link {
    font-size: 0.82rem;
    color: var(--berry, #1a3976);
    text-decoration: none;

    &:hover {
      color: var(--accent-cyan, #68d2df);
    }
  }
}

.modal.login-page-modal.show {
  z-index: 1330 !important;
  pointer-events: auto !important;

  .modal-dialog,
  .modal-content {
    filter: none !important;
    -webkit-filter: none !important;
  }
}

body:has(.login-page) > .modal-backdrop.show {
  z-index: 1324 !important;
  background-color: rgba(7, 27, 58, 0.55) !important;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  opacity: 1 !important;
}

.login-page__form {
  .form-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--text-secondary, #86868b);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .btn-primary {
    width: 100%;
    margin-top: 0.25rem;
  }
}
</style>
